<rid-mechanic-wrapper>
  <rid-top-bar class="block w-full" name="Games | Klanko" [steps]="assignments().length" [progress]="index()" />

  <div #viewport class="relative grow overflow-hidden bg-[#0d4fb3]">
    <!-- The empty studio; dims and brightens along with the lights -->
    <div
      class="absolute inset-0 bg-[url(/lingo/studio.webp)] bg-cover bg-position-[50%_40%] bg-no-repeat transition-[filter] duration-1200 ease-in-out"
      [class]="roomClass()"
    ></div>

    <!--
      Hanging from the ceiling of the viewport (not the stage, so they spread over the whole width), scaled along with the
      stage. Behind the screen so their light doesn't change the colours of the tiles.
    -->
    <div class="pointer-events-none absolute inset-0">
      @for (spotlight of SPOTLIGHTS; track $index) {
        <rid-lingo-spotlight
          class="ease-in-out-cubic duration-700"
          [class]="lampClass()"
          [style.left.px]="spotlights()[$index].left"
          [style.scale]="scale()"
          [color]="spotlight.color"
          [drop]="spotlight.drop"
          [focusAngle]="spotlights()[$index].angle"
          [mood]="spotlightMood()"
          [offset]="$index"
        />
      }
    </div>

    <!--
      The stage: the studio is laid out at this one fixed size and scaled as a whole to fit the viewport, so everything
      inside is sized with plain Tailwind. The screen with the board is on the left, the presenter's desk and the
      keyboard on the right. While a guess is checked, the keyboard is put away, the screen moves to the middle and
      the presenter steps aside to make room for it.
    -->
    <div #stage class="absolute top-1/2 left-1/2 flex h-300 w-660 -translate-1/2 items-stretch gap-10 p-10" [style.scale]="scale()">
      <!-- The screen leaves room above it for the lamps, and lines up with the keyboard at the bottom -->
      <div class="flex min-w-0 grow flex-col pt-40">
        <rid-lingo-marquee
          #screen
          class="ease-in-out-cubic min-h-0 grow transition-[translate] duration-700"
          [mood]="screenMood()"
          [bulbSpacing]="64"
          [style.translate.px]="screenShift()"
        >
          <div
            class="[container-type:size] grid size-full place-items-center rounded-3xl bg-radial-[at_50%_40%] from-[#31b9c1] to-[#27a9b3] shadow-[inset_0_8px_24px_rgb(0_0_0/0.3)]"
          >
            <!-- The tiles are as big as fits in the screen for all the rows, with some room around them so it doesn't feel cramped -->
            <div
              class="flex flex-col gap-(--gap)"
              style="
                --gap: calc(var(--spacing) * 3);
                --tile: min(
                  var(--spacing) * 39,
                  (100cqh - var(--spacing) * 12 - (var(--rows) - 1) * var(--gap)) / var(--rows),
                  (100cqw - var(--spacing) * 12 - (var(--cols) - 1) * var(--gap)) / var(--cols)
                );
              "
              [style.--cols]="answer().length"
              [style.--rows]="attempts()"
            >
              @for (row of board(); track $index) {
                <div class="flex gap-(--gap)">
                  @for (tile of row; track $index) {
                    <div [class]="tileClasses(tile)">
                      @if (tile.state !== 'EMPTY') {
                        {{ tile.klank }}
                      }
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        </rid-lingo-marquee>
      </div>

      <div #side class="relative flex shrink-0 flex-col justify-end gap-6">
        <rid-lingo-presenter
          #presenter
          class="ease-in-out-cubic grow origin-bottom-right self-center transition-[translate,scale] duration-700"
          [mood]="presenterMood()"
          [enlarged]="isFocused()"
          [style.translate]="presenterTransform().x + 'px ' + presenterTransform().y + 'px'"
          [style.scale]="presenterTransform().scale"
        />

        <!-- While a guess is checked, the keyboard slides away; once the word is guessed, the button to the next one appears below the presenter -->
        <div #controls class="grid place-items-center">
          <rid-lingo-keyboard
            class="ease-in-out-cubic transition-[translate,opacity] duration-500 [grid-area:1/1]"
            [class]="isFocused() ? 'pointer-events-none translate-y-[120%] opacity-0' : ''"
            [attr.inert]="isFocused() || null"
            [disabled]="phase() !== 'INPUT' || isRowComplete()"
            [keyStates]="keyStates()"
            [canErase]="phase() === 'INPUT' && canErase()"
            [canSubmit]="phase() === 'INPUT' && isRowComplete()"
            [marqueeMood]="marqueeMood()"
            (typed)="type($event)"
            (erased)="erase()"
            (submitted)="submit()"
          />

          @if (phase() === 'WON') {
            <!-- As high as BUTTON_ROOM in rid-lingo.ts -->
            <div
              class="z-1 grid h-40 place-items-center self-end [grid-area:1/1]"
              [style.translate]="buttonShift().x + 'px ' + buttonShift().y + 'px'"
            >
              <rid-bottom-button
                class="static! [zoom:1.6]"
                animate.enter="anim-pop-in [animation-delay:400ms]"
                [content]="isLast() ? 'Klaar!' : 'Volgende'"
                keybind="Enter"
                (clicked)="next()"
              />
            </div>
          }
        </div>
      </div>
    </div>

    <!-- reveal -->
    @if (phase() === 'LOST') {
      <div class="absolute inset-0 grid place-items-center">
        <rid-modal-background [blur]="true" />
        <rid-correct-answer-modal (continue)="next()">
          <h3 class="font-heading mb-4 text-lg">Het goede woord is:</h3>
          <div class="flex justify-center gap-2 [--tile:3.5rem]">
            @for (klank of answer(); track $index) {
              <div [class]="tileClasses({ state: 'CORRECT', klank }, false)">{{ klank }}</div>
            }
          </div>
        </rid-correct-answer-modal>
      </div>
    }

    <!-- feedback overlay -->
    <rid-overlay-feedback #overlayFeedback />
  </div>
</rid-mechanic-wrapper>
